import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const HouseholdContext = createContext({
    household: null,
    loading: true,
    isOwner: false,
    isMember: false,
    pendingRequest: null,
    memberName: () => "",
    memberById: () => null,
    refresh: async () => {},
    refreshPendingRequest: async () => {},
});

export function HouseholdProvider({ children }) {
    const { user } = useAuth();
    const [household, setHousehold] = useState(null);
    const [loading, setLoading] = useState(true);
    // The current user's own pending invite-code join request (if any) — null once
    // they're a member, or if they've never requested to join anything. Lets any page
    // show a "waiting for approval" state without each one re-fetching it separately.
    const [pendingRequest, setPendingRequest] = useState(null);

    const refreshPendingRequest = useCallback(async () => {
        if (!user) { setPendingRequest(null); return; }
        try {
            const { data } = await api.get("/household/join-requests/mine");
            setPendingRequest(data || null);
        } catch {
            setPendingRequest(null);
        }
    }, [user]);

    const refresh = useCallback(async () => {
        if (!user) { setHousehold(null); setLoading(false); return; }
        setLoading(true);
        try {
            const { data } = await api.get("/household");
            setHousehold(data || null);
        } catch {
            setHousehold(null);
        } finally {
            setLoading(false);
        }
        // A user is either a member (household set) or possibly has a pending request —
        // never both, but refresh both together so the UI can't show a stale mix.
        await refreshPendingRequest();
    }, [user, refreshPendingRequest]);

    useEffect(() => { refresh(); }, [refresh]);

    // The app has no push/websocket layer anywhere, so a requester waiting on owner
    // approval would otherwise only find out by reloading the page. Poll gently while
    // a request is pending — stops the moment it's approved/declined/cancelled.
    useEffect(() => {
        if (!pendingRequest) return;
        const id = setInterval(refresh, 20000);
        return () => clearInterval(id);
    }, [pendingRequest, refresh]);

    const isMember = !!household;
    const isOwner = !!(household && user && household.owner_id === user.id);

    const memberById = useCallback((id) => {
        if (!household) return null;
        return household.members.find(m => m.id === id) || null;
    }, [household]);

    // First-name label for attribution badges in combined views — "You" for the current user.
    const memberName = useCallback((id, fallback = "Someone") => {
        if (!user) return fallback;
        if (id === user.id) return "You";
        const m = memberById(id);
        return m ? m.name.split(" ")[0] : fallback;
    }, [memberById, user]);

    return (
        <HouseholdContext.Provider value={{ household, loading, isOwner, isMember, pendingRequest, memberName, memberById, refresh, refreshPendingRequest }}>
            {children}
        </HouseholdContext.Provider>
    );
}

export const useHousehold = () => useContext(HouseholdContext);
